import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { warn } from "@/lib/logger";

/**
 * Owns the live polling, assignment-reveal detection and current-player resolution.
 * Returns `{ room, livePlayer, reveal, clearReveal }` + the `load` function (stable
 * via useCallback) so callers can force a refresh.
 */
export function useAuctionState(roomId, { onAssigned } = {}) {
  const [room, setRoom] = useState(null);
  const [livePlayer, setLivePlayer] = useState(null);
  const [reveal, setReveal] = useState(null);
  const lastAssignedRef = useRef(null);
  const onAssignedRef = useRef(onAssigned);
  useEffect(() => { onAssignedRef.current = onAssigned; }, [onAssigned]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/rooms/${roomId}/state`);
      const last = (data.logs || []).slice().reverse().find((l) => l.type === "assigned");
      if (last && lastAssignedRef.current !== last.ts) {
        if (lastAssignedRef.current !== null && lastAssignedRef.current !== undefined) {
          onAssignedRef.current?.();
          const team = (data.teams || []).find((t) => t.name === last.team_name);
          const role = team?.players?.slice(-1)[0]?.role;
          setReveal({ ...last, role });
        }
        lastAssignedRef.current = last.ts;
      } else if (last && lastAssignedRef.current === null) {
        lastAssignedRef.current = last.ts;
      } else if (!last && lastAssignedRef.current === null) {
        lastAssignedRef.current = "";
      }
      setRoom(data);
    } catch (err) {
      warn("auction state load failed:", err);
    }
  }, [roomId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 750);
    return () => clearInterval(t);
  }, [load]);

  const auctionedPlayerId = room?.auction?.player_id;
  useEffect(() => {
    if (!auctionedPlayerId) { setLivePlayer(null); return; }
    api.get(`/players`).then((r) => {
      const p = r.data.find((x) => x.id === auctionedPlayerId);
      if (p) setLivePlayer(p);
    }).catch((err) => warn("players fetch failed:", err));
  }, [auctionedPlayerId]);

  const clearReveal = useCallback(() => setReveal(null), []);
  return { room, setRoom, livePlayer, reveal, clearReveal, reload: load };
}

export function useCountdown(endsAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(t);
  }, []);
  if (!endsAt) return null;
  const end = new Date(endsAt).getTime();
  return Math.max(0, Math.ceil((end - now) / 1000));
}
